import React, { useState, useEffect } from "react";
import { Modal, Button } from "@/components/ui";
import { fulfillQueuePurchase } from "../services/inventoryService";
import { CheckCircle2, PackageCheck, Calendar, Hash, FileText, IndianRupee, User, Store } from "lucide-react";
import api from '@/lib/API';

export const FulfillQueueModal = ({ isOpen, item, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    receivedDate: new Date().toISOString().slice(0, 10),
    quantity: 1,
    description: "",
    purchaseRate: 0,
    vendorName: "",
    projectId: "",
    projectName: ""
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (item && isOpen) {
      const firstItem = item.items?.[0] || {};
      const qty = Number(firstItem.quantity || item.quantity) || 1;
      const rate = Number(firstItem.purchaseRate || item.finalNetPayment || item.netBill) || 0;
      const desc = firstItem.description || item.description || firstItem.itemName || "";

      const pid = item.projectId || "";
      const pName = item.projectName || "";

      setFormData({
        receivedDate: new Date().toISOString().slice(0, 10),
        quantity: qty,
        description: desc,
        purchaseRate: rate,
        vendorName: item.vendorName || (typeof item.vendor === 'object' ? item.vendor?.vendorName : "") || "",
        projectId: pid,
        projectName: pName
      });
      setError(null);

      if (pid && !pName) {
        api.get(`/Generic-contacts?search=${encodeURIComponent(pid)}`).then(res => {
          const contacts = res?.data?.contacts || res?.data?.data || [];
          if (contacts.length > 0) {
            const c = contacts[0];
            const name = c.customerName || `${c.firstName || ''} ${c.lastName || ''}`.trim() || '';
            if (name) {
              setFormData(prev => ({ ...prev, projectName: name }));
            }
          }
        }).catch(() => {});
      }
    }
  }, [item, isOpen]);

  if (!isOpen || !item) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await fulfillQueuePurchase(item._id, {
        receivedDate: formData.receivedDate,
        quantity: Number(formData.quantity) || 1,
        description: formData.description,
        purchaseRate: Number(formData.purchaseRate) || 0,
        vendorName: formData.vendorName,
        projectId: formData.projectId || "",
        projectName: formData.projectName || "Generic Kitchen Project"
      });
      setLoading(false);
      onSuccess && onSuccess();
    } catch (err) {
      setLoading(false);
      setError(err?.response?.data?.message || err?.message || "Failed to fulfill queue item");
    }
  };

  const totalAmount = (Number(formData.quantity) || 0) * (Number(formData.purchaseRate) || 0);

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Fulfill Queue Purchase Item">
      <form onSubmit={handleSubmit} className="space-y-4 p-1">
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 flex items-start gap-3">
          <PackageCheck className="text-emerald-600 flex-shrink-0 mt-0.5" size={18} />
          <div className="text-xs text-emerald-800">
            <span className="font-bold block mb-0.5">Fulfill & Convert Queue Item</span>
            This will change the status from <span className="font-bold">Queue</span> to <span className="font-bold">Normal Purchase</span> and automatically add the items to the <span className="font-bold">Stock</span>.
          </div>
        </div>

        {(formData.projectId || formData.projectName) && (
          <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-3 flex items-center gap-3">
            <FileText className="text-indigo-500 flex-shrink-0" size={16} />
            <div className="text-xs">
              <span className="font-bold text-indigo-700 block">Project / Customer</span>
              <div className="flex items-center gap-2 mt-0.5">
                {formData.projectId && (
                  <span className="text-[10px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-100 px-2 py-0.5 rounded border border-indigo-200">
                    {formData.projectId}
                  </span>
                )}
                {formData.projectName && <span className="text-slate-600">{formData.projectName}</span>}
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="bg-rose-50 border border-rose-200 text-rose-700 text-xs p-2.5 rounded-lg">
            {error}
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
              <Calendar size={12} className="text-slate-400" />
              Received Date
            </label>
            <input
              type="date"
              name="receivedDate"
              value={formData.receivedDate}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
              <Hash size={12} className="text-slate-400" />
              Quantity
            </label>
            <input
              type="number"
              min="1"
              name="quantity"
              value={formData.quantity}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
            />
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
            <FileText size={12} className="text-slate-400" />
            Item Description
          </label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            required
            placeholder="Enter item name or description"
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
              <IndianRupee size={12} className="text-slate-400" />
              Purchase Rate (₹)
            </label>
            <input
              type="number"
              step="any"
              min="0"
              name="purchaseRate"
              value={formData.purchaseRate}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
              <Store size={12} className="text-slate-400" />
              Vendor / Supplier
            </label>
            <input
              type="text"
              name="vendorName"
              value={formData.vendorName}
              onChange={handleChange}
              placeholder="Vendor Name"
              className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
            />
          </div>
        </div>

        {/* <div>
          <label className="block text-xs font-bold text-slate-700 mb-1 flex items-center gap-1">
            <User size={12} className="text-slate-400" />
            Project / Customer Name (for Sales entry)
          </label>
          <input
            type="text"
            name="projectName"
            value={formData.projectName}
            onChange={handleChange}
            required
            placeholder="e.g. Generic Kitchen Project / Client Name"
            className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:border-brand-blue"
          />
        </div> */}

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 flex justify-between items-center">
          <span className="text-xs font-bold text-slate-600">Calculated Net Amount</span>
          <span className="text-sm font-black text-brand-blue">
            ₹{totalAmount.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        </div>

        <div className="flex justify-end gap-2 pt-3 border-t border-slate-100">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            variant="primary"
            size="sm"
            disabled={loading}
            className="flex items-center gap-1.5"
          >
            <CheckCircle2 size={14} />
            {loading ? "Fulfilling..." : "Fulfill & Add to Stock"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
